import { mapKeys, pick, omit, values, keys } from 'lodash';
import { FormatLinkFields, Platform, Format, AdEnvironment } from '../types';
import { stringify } from './utilities';

export const NOT_ENCODABLE_KEYS = ['campaign', 'adgroup', 'creative', 'label', 'idfa', 'gps_adid'] as const;
export const ULINK_PARAM_PREFIX = 'adj_';

/**
 * Get link domain (global or residency based)
 */
const getBaseUrl = (data: FormatLinkFields) => {
  const residencyDomain = data.dataResidency ? `${data.dataResidency}.` : '';
  return `https://app.${residencyDomain}adjust.com`;
};

/**
 * Get deep_link parameter value
 */
const getDeepLink = (data: FormatLinkFields) => {
  if (data.format === Format.tdl) {
    switch (data.platform) {
      case Platform.android:
        return (data.androidAppScheme || '') + (data.androidDeeplinkPath || '');
      case Platform.ios:
        return (data.iosAppScheme || '') + (data.iosUniversalLinkPath || '');
      case Platform.multi:
        return (data.commonAppScheme || '') + (data.commonDeeplinkPath || '');
    }
  }

  // If android app schema is prefilled from dashboard and android deeplink path is the same as ios path
  // android deeplink shouldn't be included in generated link because it will be auto filled by the backend
  if (
    data.platform === Platform.multi &&
    data.androidAppScheme &&
    !(data.androidAppSchemePrefilled && data.iosUniversalLinkPath === data.androidDeeplinkPath)
  ) {
    return data.androidAppScheme + (data.androidDeeplinkPath || '');
  }

  return '';
};

/**
 * Get deeplink_js parameter value
 */
const getDeeplinkJs = (data: FormatLinkFields) => {
  switch (data.adEnvironment) {
    case AdEnvironment.organicSnapchatChats:
      return data.format === Format.tdl ? '1' : '';

    case AdEnvironment.organicTwitter:
      return '1';

    case AdEnvironment.telegram:
      return [Format.tdl, Format.jsr].includes(data.format) ? '1' : '';
  }

  return '';
};

/**
 * Get common parameters for all formats
 */
const getCommonParams = (data: FormatLinkFields) => {
  const result: { [key: string]: string } = {
    deep_link: encodeURIComponent(getDeepLink(data)),
    campaign: data.campaign || '',
    adgroup: data.adgroup || '',
    creative: data.creative || '',
    label: data.label || '',
    fallback: data.fallbackUrl ? encodeURIComponent(data.fallbackUrl) : '',
    redirect: data.redirectUrl ? encodeURIComponent(data.redirectUrl) : '',
    redirect_macos: data.redirectMacos ? encodeURIComponent(data.redirectMacos) : '',
    redirect_ios: data.redirectIos ? encodeURIComponent(data.redirectIos) : '',
    redirect_android: data.redirectAndroid ? encodeURIComponent(data.redirectAndroid) : '',
  };

  if ([Platform.ios, Platform.multi].includes(data.platform)) {
    result.idfa = data.idfa || '';
    result.deeplink_js = getDeeplinkJs(data);
  }

  if ([Platform.android, Platform.multi].includes(data.platform)) {
    result.gps_adid = data.gpsAdid || '';
  }

  return result;
};

/**
 * Transforms object to url query string with param prefix
 * and & and = divider character encoding if necessary
 */
const stringifyUlinkParams = (params: { [key: string]: string | undefined }, partialEncode = false) => {
  const adjParams = mapKeys(params, (_, key) => `${ULINK_PARAM_PREFIX}${key}`);
  return stringify(adjParams, { partialEncode });
};

/**
 * Format link by tdl format
 */
const formatTdl = (data: FormatLinkFields) => {
  const params = {
    ...getCommonParams(data),
    ...data.additionalParameters,
  };
  return `${getBaseUrl(data)}/${data.trackerToken}?${stringify(params)}`;
};

/**
 * Format link by ulink format
 */
const formatUlink = (data: FormatLinkFields, params?: { [key: string]: string | undefined }) => {
  const { rawUniversalLink } = data;
  if (!rawUniversalLink) {
    throw new Error('`rawUniversalLink` is required');
  }

  const path = data.iosUniversalLinkPath ? `/${data.iosUniversalLinkPath}` : '';
  const connector = path && path.indexOf('?') !== -1 ? '&' : '?';
  const base = `https://${rawUniversalLink}${path}${connector}`;

  if (!params) {
    params = {
      ...getCommonParams(data),
      ...data.additionalParameters,
    };
  }

  const queryParams = stringifyUlinkParams({
    t: data.trackerToken,
    ...params,
  });

  return base + queryParams;
};

/**
 * Format link by jsr format
 */
const formatJsr = (data: FormatLinkFields) => {
  const base = `${getBaseUrl(data)}/jsr?url=`;

  // Some of the url params shouldn't be encoded because they can contain macroses.
  // Values for such keys should be passed as is.
  const commonParams = getCommonParams(data);
  let partialUlink = formatUlink(data, omit(commonParams, NOT_ENCODABLE_KEYS));
  let encodedAdditionalParameters;
  // Encode only "=" in referrer field
  if (data.additionalParameters && data.additionalParameters.referrer) {
    encodedAdditionalParameters = {
      ...data.additionalParameters,
      referrer: data.additionalParameters.referrer.replace('%3D', '%253D'),
    };
  } else {
    encodedAdditionalParameters = { ...data.additionalParameters };
  }
  const rawParams = {
    ...pick(commonParams, NOT_ENCODABLE_KEYS),
    ...encodedAdditionalParameters,
  };
  const rawQueryString = stringifyUlinkParams(rawParams, true);
  if (rawQueryString) partialUlink += '&';

  return base + encodeURIComponent(partialUlink) + rawQueryString;
};

const FORMATTERS = {
  [Format.tdl]: formatTdl,
  [Format.ulink]: formatUlink,
  [Format.jsr]: formatJsr,
};

/**
 * Format link by provided format
 */
export const formatLink = (data: FormatLinkFields) => {
  const formatLink = FORMATTERS[data.format];
  return formatLink(data);
};
